<script setup lang="ts">
import type { OutputData } from '@editorjs/editorjs';
import {
  JOURNAL_ADVERTISING_URL,
  parseJournalSectionPath,
} from '~/shared/lib/journal-routes.const';
import { useJournalIssue } from '../lib/useJournalData';
import { useJournalIssueSeo } from '../lib/seo';
import JournalCover from './JournalCover.vue';
import JournalIssueButtons from './JournalIssueButtons.vue';
import JournalAdvertisingContacts from './JournalAdvertisingContacts.vue';
import JournalArchive from './JournalArchive.vue';

const route = useRoute();

/**
 * '/journal/cre-industrial/i881' — the number in the address is the issue's own
 * id. The import kept the ids of the old panel, so the endpoint is asked for it
 * directly instead of the whole edition being searched through
 */
const issueId = computed(() => {
  const parsed = parseJournalSectionPath(route.path);

  return parsed.kind === 'issue' ? parsed.issueId : null;
});

const { issue, isLoading, error } = await useJournalIssue(() => issueId.value);

useJournalIssueSeo(issue);

/**
 * "Читайте в номере" as html. The admin writes it in EditorJS, which saves
 * JSON rather than markup, so the blocks are turned back into the tags `.text`
 * is styled for. The journal preset of the editor allows only paragraphs and
 * one heading level, so those two are all there is to translate — and the
 * article renderer is not used on purpose: it brings the typography of a news
 * article into a compact block beside the cover.
 *
 * A description that is not JSON is html from before the editor and goes out
 * as it is. An editor document with no text left in it hides the section
 * instead of leaving a heading over nothing
 */
const descriptionHtml = computed(() => {
  const raw = issue.value?.description;

  if (!raw) return '';

  try {
    const parsed = JSON.parse(raw) as OutputData;

    if (!Array.isArray(parsed?.blocks)) return raw;

    return parsed.blocks
      .map((block) => {
        const text = String(block.data?.text ?? '').trim();

        if (!text) return '';
        if (block.type === 'header') return `<h2>${text}</h2>`;
        if (block.type === 'paragraph') return `<p>${text}</p>`;

        return '';
      })
      .join('');
  } catch {
    return raw;
  }
});

/**
 * The 404 waits for the answer: a missing issue is what the endpoint says, not
 * something the address alone can tell — every /journal/<slug>/i<id> is
 * well-formed until the server has looked. On the client the answer comes
 * after mount, where a throw would not reach the error page
 */
watchEffect(() => {
  if (isLoading.value || issue.value) return;

  if (error.value || issueId.value === null) {
    const notFound = createError({
      status: 404,
      message: 'Выпуск не найден',
      fatal: true,
    });

    if (import.meta.server) throw notFound;
    showError(notFound);
  }
});
</script>

<template>
  <div v-if="isLoading" :class="$style.loader">
    <Loader />
  </div>
  <div v-else-if="issue" :class="$style.page">
    <div :class="$style.top">
      <div :class="$style.header">
        <ButtonBack :class="$style.back" />
        <JournalCover
          :title="issue.title"
          :src="issue.cover?.file_url"
          :class="$style.cover"
        />
        <div :class="$style.summary">
          <h1 :class="$style.title">{{ issue.title }}</h1>
          <p v-if="issue.short_description" :class="$style.description">
            {{ issue.short_description }}
          </p>
          <JournalIssueButtons
            :issue="issue"
            size="standard"
            :label-size="14"
            :class="$style.buttons"
          >
            <Button
              theme="secondary"
              size="standard"
              :to="JOURNAL_ADVERTISING_URL"
              :class="$style.advertising"
            >
              <span>Разместить рекламу</span>
              <Icon name="my-icon:i-arrow-right" :class="$style.icon" />
            </Button>
          </JournalIssueButtons>

          <section v-if="descriptionHtml" :class="$style.content">
            <h2 :class="$style.contentHeading">Читайте в номере:</h2>
            <!-- Built from the editor blocks above: paragraphs, one heading level, inline links -->
            <div :class="$style.text" v-html="descriptionHtml" />
          </section>
        </div>
      </div>
    </div>

    <JournalAdvertisingContacts />
    <JournalArchive :edition="issue.edition" />
  </div>
</template>

<style module lang="scss">
.page {
  display: flex;
  flex-direction: column;
  flex: 1;
  background-color: var(--color-white);
}

.loader {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 64px);
}

.top {
  display: flex;
  flex-direction: column;
  padding: var(--space-4x) var(--space-2-5x) var(--space-6x);
  background-color: var(--color-gray-bg-2);
}

/*
 * The cover holds the left column on its own — the description runs beside it
 * and past its bottom, rather than starting under it. Both columns keep the
 * proportion of the layout, where the cover takes about a third of the width.
 * Areas rather than source order, because the mobile layout restacks them:
 * there the back link stands above the cover
 */
.header {
  display: grid;
  grid-template-columns: minmax(0, 32%) minmax(0, 1fr);
  grid-template-areas:
    'cover back'
    'cover summary';
  align-items: start;
  column-gap: var(--space-5x);
  row-gap: var(--space-1-5x);
}

.back {
  grid-area: back;
  justify-self: start;
}

.cover {
  grid-area: cover;
}

.summary {
  grid-area: summary;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.title {
  @include text_h1;
  color: var(--color-black-1);
  margin: 0;
}

// "Body 0 Medium" of the layout: Golos Text 18/500, 120%, Black/2
.description {
  @include text_body1_large_medium;
  color: var(--color-black-2);
  margin: var(--space-2x) 0 0;
}

// The gap before the buttons belongs to the buttons, not to the description:
// a description is optional, and while it carried the gap an issue without one
// put its buttons flush against the title
.buttons {
  margin-top: var(--space-3x);
}

// The three buttons sit in one row and wrap together on narrow screens
.advertising > span {
  @include text_button2;
  display: inline-flex;
  align-items: center;
  gap: var(--space-1x);
  white-space: nowrap;
}

.icon {
  width: 20px;
  height: 20px;

  // The shared arrow ships with a hardcoded white fill — follow the button text
  path {
    fill: currentColor;
  }
}

.content {
  width: 100%;
  margin-top: var(--space-5x);
}

.contentHeading {
  @include text_h3;
  color: var(--color-black-1);
  margin: 0 0 var(--space-3x);
}

.text {
  @include text_body1;
  color: var(--color-black-1);
  line-height: 1.4;

  h2,
  h3 {
    @include text_body1_bold;
    margin: var(--space-3x) 0 var(--space-1x);

    &:first-child {
      margin-top: 0;
    }
  }

  p {
    margin: 0 0 var(--space-2x);

    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    color: var(--color-red-1);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

@media (max-width: 767px) {
  .title {
    @include text_h5;
  }

  .contentHeading {
    @include text_h3_mobile;
  }
}

@media (max-width: 599px) {
  .top {
    padding: var(--space-2-5x) var(--space-2x) var(--space-4x);
  }

  .header {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      'back'
      'cover'
      'summary';
    // The layout leaves the cover breathing room on both sides
    row-gap: var(--space-4x);
  }

  .cover {
    justify-self: center;
    max-width: 200px;
  }

  // Stretched so the stacked buttons fill the column, as in the layout.
  // The cover stands well clear of the title, wider than the row gap above it
  .summary {
    align-self: stretch;
    margin-top: var(--space-4x);
  }
}
</style>
